import React from 'react';
import { cn } from '../utils/cn';

/**
 * Input Component - Atom
 * 
 * @param {Object} props
 * @param {string} props.size - 'sm' | 'md' | 'lg'
 * @param {string} props.variant - 'default' | 'error' | 'success'
 * @param {string} props.type - Input type
 * @param {boolean} props.disabled - Disabled state
 * @param {React.ReactNode} props.leftIcon - Icon on the left
 * @param {React.ReactNode} props.rightIcon - Icon on the right
 */
const Input = React.forwardRef(
  (
    {
      size = 'md',
      variant = 'default',
      className = '',
      leftIcon,
      rightIcon,
      disabled = false,
      ...props
    },
    ref
  ) => {
    const baseStyles = 'w-full rounded-xl border-2 transition-all duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

    const variants = {
      default: 'border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 placeholder-primary-400 dark:placeholder-primary-500 focus:border-secondary-500 dark:focus:border-secondary-400 focus:ring-secondary-500/50 dark:focus:ring-secondary-400/50 shadow-[inset_0_1px_2px_0_rgba(0,0,0,0.05),0_1px_2px_0_rgba(0,0,0,0.05)] dark:shadow-[inset_0_1px_2px_0_rgba(0,0,0,0.3),0_1px_2px_0_rgba(0,0,0,0.2)] hover:shadow-[inset_0_1px_2px_0_rgba(0,0,0,0.05),0_2px_4px_0_rgba(0,0,0,0.06)] dark:hover:shadow-[inset_0_1px_2px_0_rgba(0,0,0,0.3),0_2px_4px_0_rgba(0,0,0,0.3)] focus:shadow-[inset_0_1px_2px_0_rgba(0,0,0,0.05),0_4px_6px_-1px_rgba(0,0,0,0.08)] dark:focus:shadow-[inset_0_1px_2px_0_rgba(0,0,0,0.3),0_4px_6px_-1px_rgba(0,0,0,0.4)]',
      error: 'border-error-400 dark:border-error-600 bg-error-50 dark:bg-error-900/30 text-error-900 dark:text-error-50 placeholder-error-400 dark:placeholder-error-500 focus:border-error-500 dark:focus:border-error-500 focus:ring-error-500/50 dark:focus:ring-error-500/50 shadow-[inset_0_1px_2px_0_rgba(0,0,0,0.05),0_1px_2px_0_rgba(0,0,0,0.05)] dark:shadow-[inset_0_1px_2px_0_rgba(0,0,0,0.3),0_1px_2px_0_rgba(0,0,0,0.2)]',
      success: 'border-success-400 dark:border-success-600 bg-success-50 dark:bg-success-900/30 text-success-900 dark:text-success-50 placeholder-success-400 dark:placeholder-success-500 focus:border-success-500 dark:focus:border-success-500 focus:ring-success-500/50 dark:focus:ring-success-500/50 shadow-[inset_0_1px_2px_0_rgba(0,0,0,0.05),0_1px_2px_0_rgba(0,0,0,0.05)] dark:shadow-[inset_0_1px_2px_0_rgba(0,0,0,0.3),0_1px_2px_0_rgba(0,0,0,0.2)]',
    };

    const sizes = {
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-2.5 text-base',
      lg: 'px-5 py-3 text-lg',
    };

    const hasIcons = leftIcon || rightIcon;

    return (
      <div className="relative">
        {leftIcon && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary-400 dark:text-primary-500">
            {leftIcon}
          </div>
        )}
        <input
          ref={ref}
          disabled={disabled}
          className={cn(
            baseStyles,
            variants[variant],
            sizes[size],
            hasIcons && leftIcon && 'pl-10',
            hasIcons && rightIcon && 'pr-10',
            // Date input specific styling
            (props.type === 'date' || props.type === 'datetime-local' || props.type === 'time' || props.type === 'month' || props.type === 'week') && 'cursor-pointer',
            className
          )}
          {...props}
        />
        {rightIcon && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-primary-400 dark:text-primary-500">
            {rightIcon}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
